import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const { data } = await API.get("/technicians/me/notifications?limit=50");
      setNotifications(data.notifications);
      setUnread(data.unread_count);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const markRead = async (id) => {
    try {
      await API.put(`/technicians/me/notifications/${id}/read`);
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    try {
      await API.put("/technicians/me/notifications/read-all");
      fetchNotifications();
      toast.success("All marked as read");
    } catch (err) {
      toast.error("Failed");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-zinc-900">Notifications</h2>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">{unread} new</Badge>
          )}
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-auto p-1"
              data-testid="mark-all-read-button">
              <CheckCheck className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card className="border border-zinc-200">
          <CardContent className="p-8 text-center">
            <Inbox className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No notifications</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => (
            <Card
              key={n.id}
              className={`border cursor-pointer transition-colors ${!n.is_read ? "border-rose-200 bg-rose-50/30" : "border-zinc-200"}`}
              onClick={() => !n.is_read && markRead(n.id)}
              data-testid={`notification-${n.id}`}
            >
              <CardContent className="p-4 flex items-start gap-3">
                <div className={`mt-0.5 ${!n.is_read ? "text-rose-500" : "text-zinc-400"}`}>
                  <Bell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${!n.is_read ? "text-zinc-900" : "text-zinc-600"}`}>{n.title}</p>
                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
